import React, { useState } from "react";
import { api, formatBRL } from "../lib/api";
import { XCircle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Diálogo de cancelamento de operação — compartilhado entre a lista de
 * Operações e o Dashboard "Recentes" (item 23b-iii).
 *
 * POST /operations/{id LOCAL}/cancel — o id vem da linha normalizada pelo
 * proxy (id local quando sincronizada); o backend cancela na V2 PRIMEIRO e
 * só então marca o espelho local (fail-closed). Erro da V2 sobe com a razão
 * no `detail` e NADA muda — o toast mostra a razão ao usuário.
 *
 * Props:
 * - operation: alvo (null fecha o diálogo)
 * - onClose(): limpar o alvo no pai
 * - onCancelled(): recarregar a lista no pai após sucesso
 */
export default function CancelOperationDialog({ operation, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const close = () => {
    setReason("");
    onClose();
  };

  const confirm = async () => {
    if (!operation) return;
    setCancelling(true);
    try {
      await api.post(`/operations/${operation.id}/cancel`, { reason: reason || null });
      toast.success(`Operação ${operation.numero} cancelada`);
      close();
      onCancelled();
    } catch (e) {
      // Razão da V2 (ex.: "não está mais aguardando aprovação") vem no detail.
      toast.error(e?.response?.data?.detail || "Falha ao cancelar operação");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={!!operation} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent data-testid="cancel-operation-dialog">
        <DialogHeader>
          <DialogTitle>Cancelar Operação</DialogTitle>
          <DialogDescription>
            Confirma o cancelamento da operação <strong>{operation?.numero}</strong> no
            valor de face de <strong>{formatBRL(operation?.valor)}</strong> para{" "}
            {operation?.cedente_nome}? O cancelamento é feito no sistema central (V2) e é
            irreversível.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
          <Textarea
            id="cancel-reason"
            placeholder="Informe o motivo do cancelamento..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1.5"
            rows={3}
            data-testid="cancel-reason-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="cancel-op-dismiss-btn">
            Voltar
          </Button>
          <Button
            variant="destructive"
            onClick={confirm}
            disabled={cancelling}
            data-testid="cancel-op-confirm-btn"
          >
            {cancelling ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <XCircle className="h-4 w-4 mr-2" />
            )}
            Confirmar Cancelamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
